import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import classNames from 'classnames';
import { getGlobalStyleSheets } from '../../../global-styles';
import { booleanAttributeConverter } from '../../helpers';
import { SpinnerSize, SpinnerAlignment } from '../../spinner';
import { AtlSelectAsyncMultiProps, AtlSelectOption } from './props';
import '../../spinner/inline-loader.component';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import accessibilityHelper from '../../../accessibilityHelper';
import {
   Sizes,
   SizesValuesMap,
   enumAttributeConverter,
} from '../../enum-types';

import * as TagNames from './select.tagnames';
import { AtlSelectChangeEvent, AtlSelectKeyDownEvent } from './select.events';

@customElement(TagNames.SelectAsyncMulti)
export class AtlSelectAsyncMulti
   extends LitElement
   implements AtlSelectAsyncMultiProps
{
   declare internalId: string;
   declare name: string;
   declare size?: Sizes;
   declare placeholder?: string;
   declare selectClassName?: string;
   declare selectValue: Array<AtlSelectOption>;
   declare options: Array<AtlSelectOption> | undefined;
   declare selectInactive?: boolean;
   declare protected loading: boolean;
   declare protected toggled: boolean;
   declare protected value: string;

   static properties = {
      internalId: { type: String },
      selectClassName: { type: String },
      name: { type: String },
      size: {
         type: String,
         convertor: enumAttributeConverter(Sizes, Sizes.Medium),
      },
      placeholder: { type: String },
      selectValue: { type: Array<AtlSelectOption[]>(), state: true },
      options: { type: Array<AtlSelectOption[]>(), state: true },
      selectInactive: { type: String, converter: booleanAttributeConverter() },
      loading: { type: Boolean, state: true },
      toggled: { type: Boolean, state: true },
      value: { type: String, state: true },
   };

   constructor() {
      super();
      this.selectClassName = '';
      this.name = '';
      this.selectValue = [];
      this.value = '';
   }

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         .dropdown-item:hover {
            cursor: pointer;
         }
         .atl-wc-dropdown-item-text {
            word-wrap: break-word;
            white-space: normal;
            word-break: break-word;
         }
      `,
   ];

   renderOptionsToShow() {
      let renderSlot = true;
      if (this.options) {
         this.loading = false;
         const optionsToRender = this.options.filter(
            (o) => !this.selectValue.some((sv) => sv.value === o.value)
         );

         if (optionsToRender.length > 0) {
            return html`
               ${this.options
                  .filter(
                     (o) => !this.selectValue.some((sv) => sv.value === o.value)
                  )
                  .map((o) => this.renderOption(o))}
            `;
         }
         renderSlot = false;
      }
      if (this.loading) {
         return html`
            <atlantis-inline-loader
               size=${SpinnerSize.small}
               align=${SpinnerAlignment.TopCenter}
            >
            </atlantis-inline-loader>
         `;
      }

      return html`
         <li
            class=${classNames(
               'dropdown-item p-2',
               SizesValuesMap[this.size ?? Sizes.Medium]
            )}
            data-type="user-profile-menu-item"
            tabindex="0"
         >
            ${renderSlot
               ? html`<slot></slot>`
               : html`<li
                    class="dropdown-item p-2"
                    data-type="user-profile-menu-item"
                    tabindex="0"
                 >
                    No more options to choose.
                 </li>`}
         </li>
      `;
   }

   renderIcon(option: AtlSelectOption) {
      if (!option.icon) return nothing;

      return html`
         <i
            tabindex="-1"
            class=${`fa-solid fa-${option.icon} ${option.iconClassName}`}
         ></i>
      `;
   }

   renderOption(option: AtlSelectOption) {
      return html`
         <li
            id=${`${this.internalId}-${option.value}`}
            class=${classNames(
               'dropdown-item p-2',
               SizesValuesMap[this.size ?? Sizes.Medium]
            )}
            data-type="user-profile-menu-item"
            tabindex="0"
            @click=${() => this._handleSelection(option.value)}
            @keydown=${(e: KeyboardEvent) =>
               accessibilityHelper.onKeyDownWrapper(e, () =>
                  this._handleItemKeyDown(e, option.value)
               )}
         >
            <div tabindex="0">
               ${this.renderIcon(option)} ${html`${unsafeHTML(option.label)}`}
            </div>
         </li>
      `;
   }

   renderSingleInput(option: AtlSelectOption, classToApply: string) {
      const clearIcon = html`
         <i
            class="fa-solid fa-times"
            style="font-size: 1rem; position: absolute; top: 11px; right: 45px; cursor: pointer;"
            @click=${() => this.handleClear(option.value)}
            tabindex="0"
         ></i>
      `;

      return html`
                <div class=${classToApply}>
                    <input 
                        id=${this.internalId}
                        class=${classNames('form-control form-select', this.selectClassName)}
                        title=${this.name}
                        type="text"
                        value=${option.label}
                        disabled
                        tabindex="0"
                    >
                    </input>
                    ${clearIcon}
                </div>
            `;
   }

   renderSingleMultiInput(option: AtlSelectOption) {
      const clearIcon = html`
         <i
            class="fa-solid fa-times"
            style="font-size: 1rem; cursor: pointer;"
            @click=${() => this.handleClear(option.value)}
            tabindex="0"
         >
         </i>
      `;
      return html`
         <div
            style="background-color: lightgray; width: fit-content; margin: 5px; border-radius: 3px;padding: 0px 10px 0px 10px;"
         >
            ${option.label} ${clearIcon}
         </div>
      `;
   }

   renderMultiInput() {
      return html`
         <div
            id=${this.internalId}
            class=${classNames(
               'form-control form-select',
               this.selectClassName
            )}
            title=${this.name}
            disabled="${true}"
            style="display: flex; flex-wrap: wrap;"
            @click=${() => this._reOpenToggle()}
            tabindex="0"
         >
            ${html`${this.selectValue.map((sv) =>
               this.renderSingleMultiInput(sv)
            )}`}
         </div>
      `;
   }

   renderInput() {
      if (this.selectValue.length > 0) {
         return this.renderMultiInput();
      }

      return html`
         <input
            class=${classNames(
               'form-control form-select',
               this.selectClassName,
               SizesValuesMap[this.size ?? Sizes.Medium]
            )}
            title=${this.name}
            type="text"
            .value="${this.value ?? ''}"
            ?disabled=${this.selectInactive === true}
            @change=${(e: Event) =>
               this._handleOnChange((e.target as HTMLInputElement).value)}
            @keyup=${(e: KeyboardEvent) =>
               accessibilityHelper.onKeyDownWrapper(
                  e,
                  () => this._handleInputKeyUp
               )}
            placeholder=${this.placeholder ?? 'Please select...'}
            tabindex="0"
         />
      `;
   }

   render() {
      return html`
         <div class="dropdown">
            <span
               id="atl-drop-down-menu-cont"
               data-bs-toggle="dropdown"
               aria-haspopup="true"
               aria-label="avatar-button-label"
               data-type="user-profile-menu-toggle"
            >
               ${this.renderInput()}
            </span>
            <ul
               class=${classNames(
                  'dropdown-menu py-0 border border-secondary rounded w-100',
                  { show: this.toggled }
               )}
               data-bs-popper="none"
               data-type="user-profile-menu-dropdown"
            >
               ${this.renderOptionsToShow()}
            </ul>
         </div>
      `;
   }

   protected _handleOnChange = (value: string) => {
      window.addEventListener('click', (e: MouseEvent) => {
         const target = e.target as HTMLElement;
         if (target?.nodeName !== 'ATLANTIS-SELECT-ASYNC-MULTI') {
            this.toggled = false;
         }

         window.removeEventListener('click', () => {});
      });
      this.value = value;
      this.toggled = value?.length > 0;
      if (value?.length >= 4) {
         this.loading = true;
         this.options = undefined;
         this.dispatchEvent(new AtlSelectKeyDownEvent(value));
      }
   };

   protected _reOpenToggle = () => {
      window.addEventListener('click', (e: MouseEvent) => {
         const target = e.target as HTMLElement;
         if (target?.nodeName !== 'ATLANTIS-SELECT-ASYNC-MULTI') {
            this.toggled = false;
         }

         window.removeEventListener('click', () => {});
      });
      this.toggled = true;
      this.loading = true;
      this.options = undefined;
      this.dispatchEvent(new AtlSelectKeyDownEvent(this.value));
   };

   protected _handleInputKeyUp = (e: KeyboardEvent) => {
      const target = e.target as HTMLInputElement;
      if (e.key === 'Enter' && target) {
         this._handleOnChange(target.value);
      }
   };

   protected _handleSelection = (value: string) => {
      const selectedValue =
         (this.options && this.options.find((o) => o.value === value)) ??
         undefined;
      if (selectedValue) {
         this.selectValue = [...this.selectValue, selectedValue];
      }
      this.toggled = true;
      this.dispatchEvent(new AtlSelectChangeEvent(this.selectValue));
   };

   protected _handleItemKeyDown = (e: KeyboardEvent, value: string) => {
      if (e.key === 'Enter') {
         this._handleSelection(value);
      }
   };

   protected handleClear = (value: string) => {
      this.selectValue = this.selectValue.filter((sv) => sv.value !== value);
      this.dispatchEvent(new AtlSelectChangeEvent(this.selectValue));
   };
}
